import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { deleteUser, getUsers, User, UserListParams } from '../api/users';
import { CommonTablePagination, MaskedEmailDisplay, SearchControlsLayout, TimeDisplay } from '../components/common';
import { DeleteUserDialog } from '../components/user';
import { NURSE_TYPE_THEME } from '../types/nurse';
import { PATIENT_THEME } from '../types/patient';
import { RESIGNATION_STATUS_DISPLAY, ResignationStatus } from '../types/common';
import { useAuth } from '../hooks/useAuth';
import { formatDateToISO } from '../utils/dateUtils';

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuth();
  const [ users, setUsers ] = useState<User[]>([]);
  const [ loading, setLoading ] = useState(true);
  const [ totalCount, setTotalCount ] = useState(0);
  const [ deleteDialogOpen, setDeleteDialogOpen ] = useState(false);
  const [ userToDelete, setUserToDelete ] = useState<User | null>(null);
  const canDeleteUser = (user: User): boolean => {
    return isAdmin() &&
      user.registration_status === 'provisional' &&
      !user.resigned_at;
  };
  // URLクエリパラメータから初期値を取得
  const searchParamsFromUrl = new URLSearchParams(location.search);
  // sessionStorageから保存された検索条件を復元
  const getSavedSearchParams = () => {
    const saved = sessionStorage.getItem('userListSearchParams');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved search params:', e);
        return null;
      }
    }
    return null;
  };
  // URLパラメータがない場合はsessionStorageから復元
  const savedParams = !location.search ? getSavedSearchParams() : null;
  const [ page, setPage ] = useState(() => {
    const pageParam = searchParamsFromUrl.get('page');
    if (pageParam) return parseInt(pageParam, 10) - 1;
    if (savedParams?.page !== undefined) return savedParams.page;
    return 0;
  });
  const [ rowsPerPage, setRowsPerPage ] = useState(() => {
    const sizeParam = searchParamsFromUrl.get('page_size');
    if (sizeParam) return parseInt(sizeParam, 10);
    if (savedParams?.rowsPerPage !== undefined) return savedParams.rowsPerPage;
    return 25;
  });
  const [ orderBy, setOrderBy ] = useState<string>(() => {
    const orderParam = searchParamsFromUrl.get('ordering');
    if (orderParam) {
      return orderParam.startsWith('-') ? orderParam.substring(1) : orderParam;
    }
    if (savedParams?.orderBy) return savedParams.orderBy;
    return 'created_at';
  });
  const [ order, setOrder ] = useState<'asc' | 'desc'>(() => {
    const orderParam = searchParamsFromUrl.get('ordering');
    if (orderParam) return orderParam.startsWith('-') ? 'desc' : 'asc';
    if (savedParams?.order) return savedParams.order;
    return 'desc';
  });
  // 検索パラメータ（URLから復元、なければsessionStorageから）
  const [ searchEmail, setSearchEmail ] = useState(searchParamsFromUrl.get('email') || savedParams?.searchEmail || '');
  const [ userType, setUserType ] = useState<string>(searchParamsFromUrl.get('user_type') || savedParams?.userType || '');
  const [ emailConfirmed, setEmailConfirmed ] = useState<string>(() => {
    const param = searchParamsFromUrl.get('email_confirmed');
    if (param === 'true' || param === 'false') return param;
    if (savedParams?.emailConfirmed) return savedParams.emailConfirmed;
    return '';
  });
  const [ registrationStatus, setRegistrationStatus ] = useState<string>(
    searchParamsFromUrl.get('registration_status') || savedParams?.registrationStatus || 'provisional'
  );
  const [ hasLoggedIn, setHasLoggedIn ] = useState<string>(() => {
    const param = searchParamsFromUrl.get('has_logged_in');
    if (param === 'true' || param === 'false') return param;
    if (savedParams?.hasLoggedIn) return savedParams.hasLoggedIn;
    return '';
  });
  const [ createdAtStart, setCreatedAtStart ] = useState<string>(
    searchParamsFromUrl.get('created_at_start') || savedParams?.createdAtStart || ''
  );
  const [ createdAtEnd, setCreatedAtEnd ] = useState<string>(
    searchParamsFromUrl.get('created_at_end') || savedParams?.createdAtEnd || ''
  );
  const [ resignationStatus, setResignationStatus ] = useState<string>(
    searchParamsFromUrl.get('resignation_status') || savedParams?.resignationStatus || ResignationStatus.ACTIVE_ONLY
  );
  const [ searchParams, setSearchParams ] = useState<UserListParams>(() => {
    // URLまたはsessionStorageから検索パラメータを復元
    const params: UserListParams = {};
    // URLパラメータを優先、なければsessionStorageから復元した値を使用
    const email = searchParamsFromUrl.get('email') || savedParams?.searchEmail || '';
    const type = searchParamsFromUrl.get('user_type') || savedParams?.userType || '';
    const confirmed = searchParamsFromUrl.get('email_confirmed') || savedParams?.emailConfirmed || '';
    const regStatus = searchParamsFromUrl.get('registration_status') || savedParams?.registrationStatus || 'provisional';
    const loggedIn = searchParamsFromUrl.get('has_logged_in') || savedParams?.hasLoggedIn || '';
    const startDate = searchParamsFromUrl.get('created_at_start') || savedParams?.createdAtStart || '';
    const endDate = searchParamsFromUrl.get('created_at_end') || savedParams?.createdAtEnd || '';
    const resStatus = searchParamsFromUrl.get('resignation_status') || savedParams?.resignationStatus || ResignationStatus.ACTIVE_ONLY;
    if (email) params.email = email;
    if (type) params.user_type = type as 'nurse' | 'patient';
    if (confirmed) params.email_confirmed = confirmed === 'true';
    if (regStatus && regStatus !== 'all') {
      params.registration_status = regStatus as 'provisional' | 'registered';
    }
    if (loggedIn) params.has_logged_in = loggedIn === 'true';
    if (startDate) params.created_at_start = startDate;
    if (endDate) params.created_at_end = endDate;
    if (resStatus) params.resignation_status = resStatus as 'false' | 'true' | 'only';
    // デフォルト値が設定されていない場合
    if (!params.registration_status && !searchParamsFromUrl.toString() && !savedParams) {
      params.registration_status = 'provisional';
    }
    if (!params.resignation_status && !searchParamsFromUrl.toString() && !savedParams) {
      params.resignation_status = 'false';  // デフォルトは有効ユーザーのみ
    }
    return params;
  });
  // URLクエリパラメータを更新する関数
  const updateUrlParams = (params: UserListParams, pageNum: number, pageSize: number, ordering: string) => {
    const urlParams = new URLSearchParams();
    // 検索パラメータ
    if (params.email) urlParams.set('email', params.email);
    if (params.user_type) urlParams.set('user_type', params.user_type);
    if (params.email_confirmed !== undefined) urlParams.set('email_confirmed', params.email_confirmed.toString());
    if (params.registration_status) urlParams.set('registration_status', params.registration_status);
    if (params.has_logged_in !== undefined) urlParams.set('has_logged_in', params.has_logged_in.toString());
    if (params.created_at_start) urlParams.set('created_at_start', params.created_at_start);
    if (params.created_at_end) urlParams.set('created_at_end', params.created_at_end);
    if (params.resignation_status) urlParams.set('resignation_status', params.resignation_status);
    // ページング・ソート
    if (pageNum > 0) urlParams.set('page', (pageNum + 1).toString());
    if (pageSize !== 25) urlParams.set('page_size', pageSize.toString());
    if (ordering !== '-created_at') urlParams.set('ordering', ordering);
    // URLを更新（履歴に追加せずに置き換え）
    navigate(`?${urlParams.toString()}`, { replace: true });
    // sessionStorageに検索条件を保存
    const savedState = {
      searchEmail,
      userType,
      emailConfirmed,
      registrationStatus,
      hasLoggedIn,
      createdAtStart,
      createdAtEnd,
      resignationStatus,
      page: pageNum,
      rowsPerPage: pageSize,
      orderBy: ordering.startsWith('-') ? ordering.substring(1) : ordering,
      order: ordering.startsWith('-') ? 'desc' : 'asc'
    };
    sessionStorage.setItem('userListSearchParams', JSON.stringify(savedState));
  };
  // API呼び出しを行うcore関数（レンダリングに依存しない）
  const fetchUsersCore = async(params: UserListParams) => {
    setLoading(true);
    try {
      const response = await getUsers(params);
      setUsers(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error('ユーザー一覧の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };
  // 初回ロード
  useEffect(() => {
    // URLパラメータから復元した値、または初回ロード時のデフォルト値でAPI呼び出し
    const initialParams: UserListParams = {
      ...searchParams,
      page: page + 1,
      page_size: rowsPerPage,
      ordering: order === 'desc' ? `-${orderBy}` : orderBy
    };
    void fetchUsersCore(initialParams);
    // 検索条件をsessionStorageに保存
    const savedState = {
      searchEmail,
      userType,
      emailConfirmed,
      registrationStatus,
      hasLoggedIn,
      createdAtStart,
      createdAtEnd,
      resignationStatus,
      page,
      rowsPerPage,
      orderBy,
      order
    };
    sessionStorage.setItem('userListSearchParams', JSON.stringify(savedState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // 初回のみ実行
  const handleSearch = async() => {
    const params: UserListParams = {};
    if (searchEmail) params.email = searchEmail;
    if (userType) params.user_type = userType as 'nurse' | 'patient';
    if (emailConfirmed) params.email_confirmed = emailConfirmed === 'true';
    if (registrationStatus && registrationStatus !== 'all') {
      params.registration_status = registrationStatus as 'provisional' | 'registered';
    }
    if (hasLoggedIn) params.has_logged_in = hasLoggedIn === 'true';
    if (createdAtStart) params.created_at_start = createdAtStart;
    if (createdAtEnd) params.created_at_end = createdAtEnd;
    if (resignationStatus) params.resignation_status = resignationStatus as 'false' | 'true' | 'only';
    // 直接APIを呼び出して、完了後にstateを更新
    const apiParams = {
      ...params,
      page: 1,
      page_size: rowsPerPage,
      ordering: order === 'desc' ? `-${orderBy}` : orderBy
    };
    await fetchUsersCore(apiParams);
    // API呼び出し成功後にstateとURLを更新
    setSearchParams(params);
    setPage(0);
    updateUrlParams(params, 0, rowsPerPage, order === 'desc' ? `-${orderBy}` : orderBy);
  };
  const handleClear = async() => {
    // フォームをクリア
    setSearchEmail('');
    setUserType('');
    setEmailConfirmed('');
    setRegistrationStatus('provisional');
    setHasLoggedIn('');
    setCreatedAtStart('');
    setCreatedAtEnd('');
    setResignationStatus(ResignationStatus.ACTIVE_ONLY);
    // ソート順序もデフォルトに戻す
    setOrderBy('created_at');
    setOrder('desc');
    // 直接APIを呼び出し
    const newParams = {
      registration_status: 'provisional' as 'provisional',
      resignation_status: ResignationStatus.ACTIVE_ONLY as 'false'
    };
    const apiParams = {
      ...newParams,
      page: 1,
      page_size: rowsPerPage,
      ordering: '-created_at'  // デフォルトの降順
    };
    await fetchUsersCore(apiParams);
    // API呼び出し成功後にstateとURLを更新
    setSearchParams(newParams);
    setPage(0);
    // URLパラメータをクリア（デフォルト値のみ設定）
    updateUrlParams(newParams, 0, rowsPerPage, '-created_at');
  };
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    // 新しいページでAPIを呼び出し
    const apiParams = {
      ...searchParams,
      page: newPage + 1,
      page_size: rowsPerPage,
      ordering: order === 'desc' ? `-${orderBy}` : orderBy
    };
    void fetchUsersCore(apiParams);
    // URLを更新
    updateUrlParams(searchParams, newPage, rowsPerPage, order === 'desc' ? `-${orderBy}` : orderBy);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // 新しいページサイズでAPIを呼び出し
    const apiParams = {
      ...searchParams,
      page: 1,
      page_size: newRowsPerPage,
      ordering: order === 'desc' ? `-${orderBy}` : orderBy
    };
    void fetchUsersCore(apiParams);
    // URLを更新
    updateUrlParams(searchParams, 0, newRowsPerPage, order === 'desc' ? `-${orderBy}` : orderBy);
  };
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    // 新しいソート順でAPIを呼び出し
    const apiParams = {
      ...searchParams,
      page: page + 1,
      page_size: rowsPerPage,
      ordering: newOrder === 'desc' ? `-${property}` : property
    };
    void fetchUsersCore(apiParams);
    // URLを更新
    updateUrlParams(searchParams, page, rowsPerPage, newOrder === 'desc' ? `-${property}` : property);
  };
  const getUserTypeChip = (userType: string) => {
    if (userType === 'nurse') {
      return (
        <Chip
          label="看護師"
          sx={{
            backgroundColor: '#ffeef3',
            color: '#f50057',
            border: '1px solid #ffcdd2',
            height: '32px',
            fontSize: '0.95rem',
            fontWeight: 600
          }}
        />
      );
    } else if (userType === 'patient') {
      return (
        <Chip
          label="患者"
          sx={{
            backgroundColor: '#e8f5e9',
            color: '#4CAF50',
            border: '1px solid #c8e6c9',
            height: '32px',
            fontSize: '0.95rem',
            fontWeight: 600
          }}
        />
      );
    }
    return <Chip label={userType}/>;
  };
  const getRegistrationStatusDisplay = (user: User) => {
    if (user.registration_status === 'registered') {
      const detailPath = user.user_type === 'nurse' && user.nurse_id
        ? `/nurses/${user.nurse_id}`
        : user.user_type === 'patient' && user.patient_id
          ? `/patients/${user.patient_id}`
          : null;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              display: 'inline-block',
              fontWeight: 'bold',
              color: '#1b5e20',
              border: '1px solid #1b5e20',
              borderLeft: '4px solid #1b5e20',
              px: 1,
              py: 0.25,
              borderRadius: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            本登録
          </Typography>
          {detailPath && (
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                navigate(detailPath);
              }}
              endIcon={<ArrowForwardIcon fontSize="small"/>}
              sx={{
                ml: 1,
                px: 1.5,
                py: 0.5,
                minWidth: 'auto',
                fontSize: '0.8rem',
                fontWeight: 'medium',
                whiteSpace: 'nowrap',
                boxShadow: 1,
                color: user.user_type === 'nurse' ? NURSE_TYPE_THEME.NORMAL.DARK : PATIENT_THEME.MYSELF.DARK,
                borderColor: user.user_type === 'nurse' ? NURSE_TYPE_THEME.NORMAL.BORDER : PATIENT_THEME.MYSELF.BORDER,
                '&:hover': {
                  borderColor: user.user_type === 'nurse' ? NURSE_TYPE_THEME.NORMAL.DARK : PATIENT_THEME.MYSELF.DARK,
                  backgroundColor: user.user_type === 'nurse' ? `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}99` : `${PATIENT_THEME.MYSELF.BACKGROUND}99`,
                  boxShadow: 2,
                }
              }}
            >
              詳細
            </Button>
          )}
        </Box>
      );
    }
    // 仮登録
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            display: 'inline-block',
            color: '#616161',
            borderBottom: '1px dotted #9e9e9e',
            px: 1,
            py: 0.25,
            opacity: 0.8,
            whiteSpace: 'nowrap',
          }}
        >
          仮登録
        </Typography>
        {canDeleteUser(user) && (
          <Tooltip title="ユーザーを削除">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(user);
              }}
              sx={{
                color: '#c62828',
                '&:hover': {
                  backgroundColor: '#ffebee',
                  color: '#b71c1c'
                }
              }}
            >
              <DeleteIcon fontSize="small"/>
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };
  const getEmailConfirmedDisplay = (confirmedAt: string | null) => {
    if (confirmedAt) {
      // 認証済みの場合は時間表示
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            component="span"
            sx={{
              color: '#2e7d32',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              mr: 0.5
            }}
          >
            ○
          </Typography>
          <Typography
            component="span"
            sx={{
              color: '#2e7d32',
              fontSize: '0.85rem',
              mr: 0.5
            }}
          >
            (
          </Typography>
          <TimeDisplay
            timestamp={confirmedAt}
            tooltipPrefix="認証日時"
            color="#2e7d32"
            iconColor="success"
            fontSize="0.85rem"
            sx={{ display: 'inline-flex' }}
          />
          <Typography
            component="span"
            sx={{
              color: '#2e7d32',
              fontSize: '0.85rem',
              ml: 0.5
            }}
          >
            )
          </Typography>
        </Box>
      );
    } else {
      // 未認証の場合
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            component="span"
            sx={{
              color: '#c62828',
              fontWeight: 'bold',
              fontSize: '1.2rem',
              mr: 0.5
            }}
          >
            ×
          </Typography>
          <Typography
            component="span"
            sx={{
              color: '#c62828',
              fontSize: '0.9rem'
            }}
          >
            未認証
          </Typography>
        </Box>
      );
    }
  };
  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = async(userId: string) => {
    await deleteUser(userId);
    // 削除成功後、一覧を再読み込み
    await handleSearch();
  };
  return (
    <Box>
      {/* 検索フォーム */}
      <Paper
        component="form"
        sx={{ p: 3, mb: 3 }}
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          void handleSearch();
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>ユーザータイプ</InputLabel>
              <Select
                value={userType}
                onChange={(e: SelectChangeEvent) => setUserType(e.target.value)}
                label="ユーザータイプ"
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value="nurse">看護師</MenuItem>
                <MenuItem value="patient">患者</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>登録状況</InputLabel>
              <Select
                value={registrationStatus}
                onChange={(e: SelectChangeEvent) => setRegistrationStatus(e.target.value)}
                label="登録状況"
              >
                <MenuItem value="provisional">仮登録のみ</MenuItem>
                <MenuItem value="registered">本登録済みのみ</MenuItem>
                <MenuItem value="all">すべて</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="メールアドレス検索"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>退会状態</InputLabel>
              <Select
                value={resignationStatus}
                onChange={(e: SelectChangeEvent) => setResignationStatus(e.target.value)}
                label="退会状態"
              >
                <MenuItem value={ResignationStatus.ACTIVE_ONLY}>{RESIGNATION_STATUS_DISPLAY[ResignationStatus.ACTIVE_ONLY]}</MenuItem>
                <MenuItem value={ResignationStatus.INCLUDE_RESIGNED}>{RESIGNATION_STATUS_DISPLAY[ResignationStatus.INCLUDE_RESIGNED]}</MenuItem>
                <MenuItem value={ResignationStatus.RESIGNED_ONLY}>{RESIGNATION_STATUS_DISPLAY[ResignationStatus.RESIGNED_ONLY]}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* 2行目 */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
              <TextField
                label="登録日(開始)"
                type="date"
                value={createdAtStart}
                onChange={(e) => {
                  setCreatedAtStart(e.target.value);
                  // 終了日が開始日より前の場合はクリア
                  if (createdAtEnd && e.target.value > createdAtEnd) {
                    setCreatedAtEnd('');
                  }
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: formatDateToISO(new Date()) }}
                size="small"
                sx={{ width: '50%', mr: 1 }}
              />
              <TextField
                label="登録日(終了)"
                type="date"
                value={createdAtEnd}
                onChange={(e) => setCreatedAtEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: createdAtStart || undefined,
                  max: formatDateToISO(new Date())
                }}
                disabled={!createdAtStart}
                size="small"
                sx={{ width: '50%' }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>メール認証状態</InputLabel>
              <Select
                value={emailConfirmed}
                onChange={(e: SelectChangeEvent) => setEmailConfirmed(e.target.value)}
                label="メール認証状態"
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value="true">認証済み</MenuItem>
                <MenuItem value="false">未認証</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>ログイン履歴</InputLabel>
              <Select
                value={hasLoggedIn}
                onChange={(e: SelectChangeEvent) => setHasLoggedIn(e.target.value)}
                label="ログイン履歴"
              >
                <MenuItem value="">すべて</MenuItem>
                <MenuItem value="true">ログインあり</MenuItem>
                <MenuItem value="false">ログインなし</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* 検索結果件数表示と検索ボタン */}
        <SearchControlsLayout
          count={totalCount}
          onClear={handleClear}
          searchLabel="検索"
          clearLabel="クリア"
        />
      </Paper>

      {/* テーブル */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress/>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>登録状況</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'created_at'}
                      direction={orderBy === 'created_at' ? order : 'asc'}
                      onClick={() => handleRequestSort('created_at')}
                    >
                      登録日時
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>ユーザータイプ</TableCell>
                  <TableCell>メールアドレス</TableCell>
                  <TableCell>メール認証</TableCell>
                  <TableCell>最終ログイン</TableCell>
                  <TableCell>退会日時</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{getRegistrationStatusDisplay(user)}</TableCell>
                    <TableCell>
                      <TimeDisplay timestamp={user.created_at} color="text.primary"/>
                    </TableCell>
                    <TableCell>{getUserTypeChip(user.user_type)}</TableCell>
                    <TableCell>
                      <MaskedEmailDisplay email={user.email}/>
                    </TableCell>
                    <TableCell>{getEmailConfirmedDisplay(user.confirmed_at)}</TableCell>
                    <TableCell>
                      {user.current_sign_in_at ? (
                        <TimeDisplay timestamp={user.current_sign_in_at}/>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.resigned_at ? (
                        <TimeDisplay timestamp={user.resigned_at}/>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <CommonTablePagination
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </TableContainer>

      {/* 削除確認ダイアログ */}
      <DeleteUserDialog
        open={deleteDialogOpen}
        user={userToDelete}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};
export default UserList;
